import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { AlertCircle, Download, Loader2, RefreshCw, Trash2, Upload } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SnapshotsTab = ({ token }) => {
  const [snapshots, setSnapshots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [restoring, setRestoring] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [includeImages, setIncludeImages] = useState(false);
  const [label, setLabel] = useState('');
  const [restoreMode, setRestoreMode] = useState('backup'); // 'backup' or 'replace'
  const [showRestoreConfirm, setShowRestoreConfirm] = useState(null);
  const [uploadFile, setUploadFile] = useState(null);
  const [showUploadRestore, setShowUploadRestore] = useState(false);
  const fileInputRef = React.useRef();

  const fetchSnapshots = () => {
    setLoading(true);
    axios.get(`${API}/admin/snapshots`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setSnapshots(r.data.snapshots || []))
      .catch(() => toast.error('Failed to load snapshots'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchSnapshots(); }, [token]);

  const handleCreate = async () => {
    setCreating(true);
    try {
      const res = await axios.post(`${API}/admin/snapshots/create`, {
        include_images: includeImages,
        label: label || 'manual'
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Snapshot created: ${res.data.name}`);
      setLabel('');
      fetchSnapshots();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Snapshot creation failed');
    } finally {
      setCreating(false);
    }
  };

  const handleDownload = (filename) => {
    const link = document.createElement('a');
    link.href = `${API}/admin/snapshots/download/${filename}`;
    link.download = filename;
    // Need to use fetch with auth header
    fetch(`${API}/admin/snapshots/download/${filename}`, {
      headers: { Authorization: `Bearer ${token}` }
    }).then(r => r.blob()).then(blob => {
      const url = URL.createObjectURL(blob);
      link.href = url;
      link.click();
      URL.revokeObjectURL(url);
    }).catch(() => toast.error('Download failed'));
  };

  const handleRestore = async (filename, backupFirst) => {
    setRestoring(filename);
    try {
      const res = await axios.post(`${API}/admin/snapshots/restore/${filename}`, {
        backup_first: backupFirst
      }, { headers: { Authorization: `Bearer ${token}` } });
      const restored = Object.values(res.data.collections_restored || {}).reduce((a, b) => a + b, 0);
      toast.success(`Restored ${restored} documents. ${res.data.backup_created ? `Backup: ${res.data.backup_created}` : ''}`);
      if (res.data.errors?.length) toast.error(`${res.data.errors.length} errors during restore`);
      setShowRestoreConfirm(null);
      fetchSnapshots();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Restore failed');
    } finally {
      setRestoring(null);
    }
  };

  const handleDelete = async (filename) => {
    if (!window.confirm('Delete this snapshot permanently?')) return;
    try {
      await axios.delete(`${API}/admin/snapshots/${filename}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Snapshot deleted');
      fetchSnapshots();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const handleUploadRestore = async () => {
    if (!uploadFile) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', uploadFile);
      formData.append('backup_first', restoreMode === 'backup' ? 'true' : 'false');
      const res = await axios.post(`${API}/admin/snapshots/upload-restore`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      const restored = Object.values(res.data.collections_restored || {}).reduce((a, b) => a + b, 0);
      toast.success(`Restored ${restored} documents from uploaded snapshot`);
      setShowUploadRestore(false);
      setUploadFile(null);
      fetchSnapshots();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload restore failed');
    } finally {
      setUploading(false);
    }
  };

  const formatBytes = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  return (
    <div className="space-y-6" data-testid="snapshots-tab">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Site Snapshots</h2>
          <p className="text-sm text-gray-500 mt-1">Create, download, and restore full site backups. Auto-weekly snapshots are enabled.</p>
        </div>
      </div>

      {/* Create Snapshot */}
      <div className="bg-white border border-gray-200 rounded-lg p-5" data-testid="create-snapshot-section">
        <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-4">Create New Snapshot</h3>
        <div className="flex flex-wrap gap-4 items-end">
          <div className="flex-1 min-w-[200px]">
            <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Label (optional)</label>
            <input
              type="text"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="e.g. before-sale-update"
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm text-gray-900 focus:ring-2 focus:ring-blue-500"
              data-testid="snapshot-label-input"
            />
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={includeImages} onChange={(e) => setIncludeImages(e.target.checked)} className="w-4 h-4 rounded text-blue-600" data-testid="include-images-checkbox" />
            <span className="text-sm text-gray-700">Include images (~3 GB)</span>
          </label>
          <button
            onClick={handleCreate}
            disabled={creating}
            className="px-5 py-2 bg-gray-900 text-white text-sm font-semibold rounded-lg hover:bg-gray-800 disabled:opacity-50 flex items-center gap-2"
            data-testid="create-snapshot-btn"
          >
            {creating ? <><Loader2 className="w-4 h-4 animate-spin" /> Creating...</> : <><Download className="w-4 h-4" /> Create Snapshot</>}
          </button>
        </div>
      </div>

      {/* Upload & Restore */}
      <div className="bg-white border border-gray-200 rounded-lg p-5" data-testid="upload-restore-section">
        <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-4">Upload & Restore</h3>
        <p className="text-xs text-gray-500 mb-3">Upload a previously downloaded snapshot .zip file to restore the site.</p>
        <div className="flex flex-wrap gap-3 items-center">
          <input
            ref={fileInputRef}
            type="file"
            accept=".zip"
            className="hidden"
            onChange={(e) => { setUploadFile(e.target.files[0]); setShowUploadRestore(true); }}
            data-testid="upload-snapshot-file"
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            className="px-4 py-2 border-2 border-dashed border-gray-300 rounded-lg text-sm text-gray-600 hover:border-blue-400 hover:text-blue-600 transition-colors flex items-center gap-2"
            data-testid="upload-snapshot-btn"
          >
            <Upload className="w-4 h-4" /> Choose Snapshot File
          </button>
          {uploadFile && (
            <span className="text-xs text-gray-500">{uploadFile.name} ({formatBytes(uploadFile.size)})</span>
          )}
        </div>
      </div>

      {/* Upload Restore Modal */}
      {showUploadRestore && uploadFile && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="upload-restore-modal">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
            <div className="p-5 border-b">
              <h3 className="text-lg font-bold text-gray-900">Restore from Upload</h3>
              <p className="text-sm text-gray-500 mt-1">File: {uploadFile.name} ({formatBytes(uploadFile.size)})</p>
            </div>
            <div className="p-5 space-y-3">
              <p className="text-sm text-gray-700 font-medium">Restore mode:</p>
              <label className="flex items-start gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50" data-testid="restore-mode-backup">
                <input type="radio" name="restoreMode" value="backup" checked={restoreMode === 'backup'} onChange={() => setRestoreMode('backup')} className="mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-gray-900">Safe Restore (Recommended)</p>
                  <p className="text-xs text-gray-500">Creates a backup of current data before restoring. You can rollback if needed.</p>
                </div>
              </label>
              <label className="flex items-start gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50" data-testid="restore-mode-replace">
                <input type="radio" name="restoreMode" value="replace" checked={restoreMode === 'replace'} onChange={() => setRestoreMode('replace')} className="mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-gray-900">Direct Replace</p>
                  <p className="text-xs text-red-500">Overwrites current data without backup. Cannot be undone.</p>
                </div>
              </label>
            </div>
            <div className="p-5 border-t bg-gray-50 flex gap-3 justify-end rounded-b-lg">
              <button onClick={() => { setShowUploadRestore(false); setUploadFile(null); }} className="px-4 py-2 text-sm text-gray-600" data-testid="upload-restore-cancel">Cancel</button>
              <button
                onClick={handleUploadRestore}
                disabled={uploading}
                className="px-5 py-2 bg-red-600 text-white text-sm font-semibold rounded hover:bg-red-700 disabled:opacity-50"
                data-testid="upload-restore-confirm"
              >
                {uploading ? 'Restoring...' : 'Restore Now'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Snapshots List */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-testid="snapshots-list">
        <div className="px-5 py-3 border-b border-gray-100">
          <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider">Available Snapshots</h3>
        </div>
        {loading ? (
          <div className="p-8 text-center"><Loader2 className="w-5 h-5 animate-spin text-gray-400 mx-auto" /></div>
        ) : snapshots.length === 0 ? (
          <div className="p-8 text-center text-sm text-gray-400">No snapshots yet. Create one above.</div>
        ) : (
          <div className="divide-y divide-gray-100">
            {snapshots.map((s) => (
              <div key={s.filename} className="p-4 hover:bg-gray-50/50 flex items-center justify-between gap-4" data-testid={`snapshot-${s.filename}`}>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-semibold text-gray-900 truncate">{s.name}</p>
                    {s.label && s.label.includes('auto_weekly') && (
                      <span className="text-[10px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full font-medium">Auto</span>
                    )}
                    {s.label && s.label.includes('pre-restore') && (
                      <span className="text-[10px] bg-yellow-100 text-yellow-700 px-1.5 py-0.5 rounded-full font-medium">Pre-Restore Backup</span>
                    )}
                    {s.include_images && (
                      <span className="text-[10px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full font-medium">+ Images ({s.image_count})</span>
                    )}
                  </div>
                  <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                    <span>{s.created_at ? new Date(s.created_at).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}</span>
                    <span>{formatBytes(s.file_size)}</span>
                    <span>{s.total_docs?.toLocaleString()} records</span>
                    {s.collections?.products > 0 && <span>{s.collections.products.toLocaleString()} products</span>}
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button
                    onClick={() => handleDownload(s.filename)}
                    className="px-3 py-1.5 text-xs font-medium bg-gray-100 text-gray-700 rounded hover:bg-gray-200 flex items-center gap-1"
                    data-testid={`download-${s.filename}`}
                  >
                    <Download className="w-3 h-3" /> Download
                  </button>
                  <button
                    onClick={() => setShowRestoreConfirm(s.filename)}
                    disabled={!!restoring}
                    className="px-3 py-1.5 text-xs font-medium bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 flex items-center gap-1"
                    data-testid={`restore-${s.filename}`}
                  >
                    {restoring === s.filename ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
                    {restoring === s.filename ? 'Restoring...' : 'Restore'}
                  </button>
                  <button
                    onClick={() => handleDelete(s.filename)}
                    className="p-1.5 text-gray-400 hover:text-red-500 transition-colors"
                    data-testid={`delete-${s.filename}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Restore Confirm Modal */}
      {showRestoreConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="restore-confirm-modal">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
            <div className="p-5 border-b">
              <h3 className="text-lg font-bold text-gray-900">Restore Snapshot</h3>
              <p className="text-sm text-gray-500 mt-1">This will replace your current site data.</p>
            </div>
            <div className="p-5 space-y-3">
              <label className="flex items-start gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50" data-testid="confirm-mode-backup">
                <input type="radio" name="confirmMode" value="backup" checked={restoreMode === 'backup'} onChange={() => setRestoreMode('backup')} className="mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-gray-900">Safe Restore</p>
                  <p className="text-xs text-gray-500">Back up current data first, then restore.</p>
                </div>
              </label>
              <label className="flex items-start gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50" data-testid="confirm-mode-replace">
                <input type="radio" name="confirmMode" value="replace" checked={restoreMode === 'replace'} onChange={() => setRestoreMode('replace')} className="mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-gray-900">Direct Replace</p>
                  <p className="text-xs text-red-500">No backup — current data will be permanently overwritten.</p>
                </div>
              </label>
            </div>
            <div className="p-5 border-t bg-gray-50 flex gap-3 justify-end rounded-b-lg">
              <button onClick={() => setShowRestoreConfirm(null)} className="px-4 py-2 text-sm text-gray-600" data-testid="restore-confirm-cancel">Cancel</button>
              <button
                onClick={() => handleRestore(showRestoreConfirm, restoreMode === 'backup')}
                disabled={!!restoring}
                className="px-5 py-2 bg-red-600 text-white text-sm font-semibold rounded hover:bg-red-700 disabled:opacity-50"
                data-testid="restore-confirm-btn"
              >
                {restoring ? 'Restoring...' : 'Confirm Restore'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


export default SnapshotsTab;
